import { z } from 'zod';
import { ReferralStatus } from './enums.ts';
import { brandedUuid } from './ids.ts';

/**
 * Referral-worker wire contracts — the typed payloads the `apps/jobs` referral
 * worker consumes (referrals.md § "SRP boundaries": the BullMQ worker "drives
 * qualify + bonus + revshare off signup/verify/payment events, async, not inline
 * at signup"). The DOMAIN lives behind `backend/src/referrals/*`; this file owns
 * only the wire SHAPE so the producers (the `apps/api` auth route on signup /
 * age-verify, the CCBill webhook sink on a confirmed payment) and the worker
 * agree on what an event looks like.
 *
 * One discriminated union, `kind` the discriminator — four events:
 *  - `signup`  — a `?ref=` link was just attributed (`referrals(pending)`): the
 *    worker (re)runs the circular-loop screen + the qualification gate.
 *  - `verify`  — the referred user cleared the async gate (age-verified, or a
 *    distinct device fingerprint was recorded): re-run qualify → maybe `qualified`
 *    → settle the 5-batch bounty.
 *  - `payment` — the referred user PAID (a `topup` / `subscription_grant` ledger
 *    row): mint the referrer's 15% rev-share, once per `sourceLedgerId`.
 *  - `chargeback` — that paid spend charged back: reverse the matching earning.
 *
 * The payload stays MINIMAL — the worker reads authoritative state (the referral
 * row, `users.age_verified_at`, device fingerprints) from the DB, never off the
 * wire. Only signals the DB does not durably hold ride along.
 */

/** The referral event a worker payload carries. */
export const ReferralEventKind = z.enum(['signup', 'verify', 'payment', 'chargeback']);
export type ReferralEventKind = z.infer<typeof ReferralEventKind>;

/** Shared shape of the two qualification-triggering events (`signup` / `verify`). */
const qualifyEventShape = {
  /** The freshly-signed-up / just-verified referred user this event is about. */
  referredUserId: brandedUuid('UserId'),
  /** The referred user's OWN account (tenant) — where their device fingerprints live. */
  referredAccountId: brandedUuid('AccountId'),
  /**
   * Whether the signup passed the hCaptcha challenge. Defaults to `true`: the edge
   * enforces captcha synchronously at `POST /auth/register` before a referral can
   * be attributed, so any referral reaching the async worker already cleared it —
   * the gate's captcha check is a signup-edge invariant, re-asserted here. A
   * producer (or the simulator, exercising the reject path) may set it `false`.
   */
  captchaPassed: z.boolean().default(true),
} as const;

/** `signup` — a `?ref=` link was attributed; the worker screens + qualifies it. */
export const ReferralSignupEvent = z.object({
  kind: z.literal('signup'),
  ...qualifyEventShape,
});

/** `verify` — the referred user cleared the async gate; re-qualify + settle bounty. */
export const ReferralVerifyEvent = z.object({
  kind: z.literal('verify'),
  ...qualifyEventShape,
});

/** `payment` — the referred user paid; mint the referrer's 15% rev-share once. */
export const ReferralPaymentEvent = z.object({
  kind: z.literal('payment'),
  /** The referred user who paid (their referral link is looked up by this id). */
  referredUserId: brandedUuid('UserId'),
  /** The referred user's OWN account (tenant) — where the source ledger row lives; keys the durable read. */
  referredAccountId: brandedUuid('AccountId'),
  /** The referred user's `topup` / `subscription_grant` ledger row — the unique mint source. */
  sourceLedgerId: brandedUuid('CreditLedgerId'),
  /**
   * The paid spend, in credit micros — an ADVISORY hint only. The worker mints
   * from the durable ledger row's granted amount, not this wire value, so a
   * malformed producer can't inflate the share.
   */
  spendMicros: z.number().int().nonnegative(),
});

/** `chargeback` — a referred paid spend charged back; reverse the matching earning. */
export const ReferralChargebackEvent = z.object({
  kind: z.literal('chargeback'),
  /** The now-charged-back referred spend ledger row whose earning is reversed. */
  sourceLedgerId: brandedUuid('CreditLedgerId'),
});

/** The BullMQ payload the referral worker validates + processes. */
export const ReferralJobData = z.discriminatedUnion('kind', [
  ReferralSignupEvent,
  ReferralVerifyEvent,
  ReferralPaymentEvent,
  ReferralChargebackEvent,
]);
export type ReferralJobData = z.infer<typeof ReferralJobData>;

/**
 * The terminal state one processed referral event resolved to — the RESULT half
 * of the worker contract (the input half is {@link ReferralJobData}). Lives here,
 * beside the event it answers, so the backend orchestrator (producer) and the
 * `apps/jobs` worker (consumer, which returns it as the BullMQ job result) agree
 * on the shape without a backend→jobs type dependency.
 */
export const ReferralEventResult = z.enum([
  'qualified', // the referral is now qualified (counts toward the 5-batch)
  'pending', // still pending — a recoverable gate miss (age/captcha/velocity/fingerprint)
  'revoked', // fraud verdict (fingerprint collision / circular loop) — terminal
  'not_referred', // no referral for this user (an organic signup) — nothing to do
  'minted', // rev-share credited to the referrer
  'mint_skipped', // no mint (not referred / not qualified / zero spend)
  'reversed', // a charged-back spend's earning was clawed back
  'reverse_noop', // no earning to reverse (never minted, or already reversed)
]);
export type ReferralEventResult = z.infer<typeof ReferralEventResult>;

/**
 * Why the event landed where it did — the union of every qualify reason and mint
 * skip surfaced for logs + the dashboard. A superset of the backend's own
 * `QualifyReason` / `MintSkip`, so those assign into it cleanly.
 */
export const ReferralEventReason = z.enum([
  'qualified',
  'age_unverified',
  'captcha_failed',
  'velocity_hold',
  'no_fingerprint',
  'fingerprint_collision',
  'already_revoked',
  'not_referred',
  'not_qualified',
  'zero_spend',
]);
export type ReferralEventReason = z.infer<typeof ReferralEventReason>;

/** What the orchestrator reports for one processed event (the BullMQ job result). */
export const ReferralEventOutcome = z.object({
  kind: ReferralEventKind,
  outcome: ReferralEventResult,
  /** Whether THIS run changed durable state (idempotent replays report `false`). */
  changed: z.boolean(),
  /** The qualify reason / mint skip, for logs + the dashboard. */
  reason: ReferralEventReason.optional(),
  /** Sign-up bounty batches newly granted by the settle this run triggered. */
  grantedBatches: z.array(z.number().int()).readonly().optional(),
  /** Sign-up bounty batches clawed back by the settle this run triggered. */
  clawedBackBatches: z.array(z.number().int()).readonly().optional(),
  /** Credits minted (rev-share) or reversed (chargeback), in micros. */
  amountMicros: z.number().int().optional(),
});
export type ReferralEventOutcome = z.infer<typeof ReferralEventOutcome>;

/**
 * Referral dashboard WIRE CONTRACT — the Zod-backed source of truth for the
 * `GET /referrals/summary` payload, shared api ↔ backend ↔ frontend (CLAUDE.md:
 * "Validate shared contracts with Zod schemas in `@4f/shared`; use typed
 * contracts throughout the API, backend, and frontend"). The backend
 * `toReferralSummaryView` mapper PRODUCES these shapes; the frontend
 * RE-VALIDATES them at the network boundary. Display-only — the api is
 * authority on status/earnings, never the client. Money stays in micros.
 */

/** One person you referred. */
export const ReferredUserView = z.object({
  id: z.string().min(1),
  status: ReferralStatus,
  createdAt: z.string(),
});
export type ReferredUserView = z.infer<typeof ReferredUserView>;

/** One rev-share earning credited from a referred friend's spend. */
export const ReferralEarningView = z.object({
  id: z.string().min(1),
  revshareMicros: z.number(),
  createdAt: z.string(),
});
export type ReferralEarningView = z.infer<typeof ReferralEarningView>;

/** The whole referral summary the client renders verbatim — link + stats + history. */
export const ReferralSummaryView = z.object({
  /** Your shareable code — the client composes the full invite link from it. */
  referralCode: z.string().min(1),
  invitedCount: z.number(),
  qualifiedCount: z.number(),
  earnedMicros: z.number(),
  referred: z.array(ReferredUserView),
  earnings: z.array(ReferralEarningView),
  /**
   * Program config, server-authoritative (mirrors `backend/referrals/constants.ts` —
   * never re-typed client-side; the client renders whatever number the api reports).
   */
  /** Credits granted per sign-up-bounty batch (spec: "$2 in credits" ≈ 500 credits). */
  signupBonusCredits: z.number(),
  /** Qualified referrals per bounty batch (spec: every 5th). */
  signupBonusBatchSize: z.number(),
  /** Rev-share rate, as a whole percent (spec: 15%). */
  revsharePercent: z.number(),
});
export type ReferralSummaryView = z.infer<typeof ReferralSummaryView>;
